import ImageInput from "@component/common/ImageInput";
import ModalBox from "@component/common/ModalBox";
import ModalComponent from "@component/common/ModalComponent";
import styled from "styled-components";
import AddIcon from "@mui/icons-material/Add";
import ChallengeMissionComponent from "./ChallengeMissionComponent";
import MultiButton from "@component/common/MultiButton";
import RowBox from "@component/common/RowBox";
import StandardInput from "@component/common/StandardInput";
import StandardTextArea from "@component/common/StandardTextArea";
import useImageInput from "@hook/useImageInput";
import challengeRepository from "@api/challenge/challenge.repository";
import challengeMissionRepository from "@api/challenge/challenge-mission.repository";
import { ModalTitle } from "@component/common/ModalText";
import { StandardImage } from "@component/common/StandardImage";
import { useDictInput } from "@hook/useDictInput";
import { Box } from "@mui/material";
import {
  ChallengeModal,
  challengeInfoModal,
  selectedChallenge,
} from "@store/atom/challenge";
import { useRecoilState, useRecoilValue } from "recoil";
import { useState } from "react";
import {
  ChallengeAdminDto,
  ChallengeCreate,
  ChallengeMission,
  UpdateChallengeDto,
  UpdateChallengeMissionRequestDto,
} from "@interface/challenge";
import { formatDateToYYYYMMDDTHHMM } from "@util/date";
import { MissionType } from "@entity/challenge.entity";
import challengePictureRepository from "@api/challenge/challenge-picture.repository";

export default function ChallengeInfoModal() {
  const { images, setImages, fileInput, onFileUpload, handleFileChange } =
    useImageInput();
  const [deleteImages, setDeleteImages] = useState<string[]>([]);
  const [challengeInfo, setChallengeInfo] =
    useRecoilState<ChallengeModal | null>(challengeInfoModal);

  const challenge = useRecoilValue<ChallengeAdminDto | null>(selectedChallenge);
  const [missions, setMissions] = useState<ChallengeMission[]>(
    challenge?.missions
      .filter((mission) => mission.missionType === MissionType.MISSION)
      .map((mission) => {
        return {
          id: mission.id,
          title: mission.title,
          description: mission.description ?? "",
          startDate: formatDateToYYYYMMDDTHHMM(new Date(mission.startDate)),
          endDate: formatDateToYYYYMMDDTHHMM(new Date(mission.endDate)),
          missionType: MissionType[mission.missionType].toString(),
        };
      }) || []
  );

  const [deleteMissionsList, setDeleteMissionList] = useState<string[]>([]);

  const onMissionAdd = () => {
    const newMission = [...missions];
    newMission.push({
      id: null,
      title: "",
      description: "",
      startDate: "",
      endDate: "",
      missionType: MissionType.MISSION.toString(),
    });
    setMissions(newMission);
  };

  const onMissionDelete = (index: number) => {
    const newMission = [...missions];
    const deletes = newMission.splice(index, 1);
    if (deletes[0].id) {
      setDeleteMissionList([...deleteMissionsList, deletes[0].id]);
    }
    setMissions(newMission);
  };

  const onMissionChange = (
    index: number,
    key: keyof ChallengeMission,
    value: string
  ) => {
    const newMission = [...missions];
    newMission[index][key] = value;
    setMissions(newMission);
  };

  const [data, setData, onChange] = useDictInput({
    title: challenge?.challenge.title || "",
    paymentStartDate: challenge
      ? formatDateToYYYYMMDDTHHMM(
          new Date(challenge.challenge.paymentStartDate)
        )
      : "",
    paymentEndDate: challenge
      ? formatDateToYYYYMMDDTHHMM(new Date(challenge.challenge.paymentEndDate))
      : "",
    startDate: challenge
      ? formatDateToYYYYMMDDTHHMM(new Date(challenge.challenge.startDate))
      : "",
    endDate: challenge
      ? formatDateToYYYYMMDDTHHMM(new Date(challenge.challenge.endDate))
      : "",
    price: challenge?.challenge.price || "",
    pictures: challenge?.pictures || [],
    description: challenge?.challenge.description || "",
  });

  const [checkPeriod, setCheckPeriod, onChangeCheckPeriod] = useDictInput(
    getMissionCheckPeriods(challenge || null)
  );

  // 시작 시간이 한국시간(KST) 오후 6시(18:00)를 넘는지 체크하는 함수
  // datetime-local 입력 필드는 브라우저의 로컬 시간대를 사용하므로,
  // 한국에서 사용하면 자동으로 KST(UTC+9)로 해석됨
  const validateStartTime = (datetimeString: string): boolean => {
    if (!datetimeString) return true; // 빈 값은 통과

    // datetime-local 형식("YYYY-MM-DDTHH:mm")은 로컬 시간으로 해석됨
    // 한국에서 사용하면 KST 기준으로 해석되므로, getHours()는 KST 시간을 반환
    const date = new Date(datetimeString);
    const localHours = date.getHours(); // KST 기준 시간 (한국에서 사용 시)
    const localMinutes = date.getMinutes(); // KST 기준 분 (한국에서 사용 시)

    // 한국 시간 기준 오후 6시(18:00) 초과 체크
    // 참고: 서버는 UTC를 사용하지만, validation은 사용자가 입력한 KST 기준으로 체크
    // 18:00:00은 허용, 18:00:01부터는 차단
    if (localHours > 18 || (localHours === 18 && localMinutes > 0)) {
      alert("시작 시간은 한국시간(KST) 오후 6시(18:00) 이전으로 설정해주세요.");
      return false;
    }

    // 정확히 18:00:00만 허용
    return true;
  };

  // 시간 범위가 겹치는지 체크하는 함수
  const checkTimeOverlap = (
    start1: Date,
    end1: Date,
    start2: Date,
    end2: Date
  ): boolean => {
    // 두 시간 범위가 겹치는 조건: start1 < end2 && start2 < end1
    return start1 < end2 && start2 < end1;
  };

  // 점검 항목들이 순차적으로 진행되는지 체크하는 함수
  const validateCheckPeriodSequence = (
    tempCheckPeriod?: typeof checkPeriod
  ): boolean => {
    const periodToCheck = tempCheckPeriod || checkPeriod;

    // 1. 시작자료 제출의 끝 시간 < 시작자료 제출(기한 후 제출)의 시작 시간
    if (
      periodToCheck.firstCheckEndDate &&
      periodToCheck.firstSecondCheckStartDate
    ) {
      const firstCheckEnd = new Date(periodToCheck.firstCheckEndDate);
      const firstSecondCheckStart = new Date(
        periodToCheck.firstSecondCheckStartDate
      );
      if (firstCheckEnd >= firstSecondCheckStart) {
        alert(
          "시작자료 제출의 끝 시간은 시작자료 제출(기한 후 제출)의 시작 시간보다 이전이어야 합니다."
        );
        return false;
      }
    }

    // 2. 시작자료 제출(기한 후 제출)의 끝 시간 < 중간자료 제출의 시작 시간
    if (
      periodToCheck.firstSecondCheckEndDate &&
      periodToCheck.middleFirstCheckStartDate
    ) {
      const firstSecondCheckEnd = new Date(
        periodToCheck.firstSecondCheckEndDate
      );
      const middleFirstCheckStart = new Date(
        periodToCheck.middleFirstCheckStartDate
      );
      if (firstSecondCheckEnd >= middleFirstCheckStart) {
        alert(
          "시작자료 제출(기한 후 제출)의 끝 시간은 중간자료 제출의 시작 시간보다 이전이어야 합니다."
        );
        return false;
      }
    }

    // 3. 중간자료 제출의 끝 시간 < 중간자료 제출(기한 후 제출)의 시작 시간
    if (
      periodToCheck.middleFirstCheckEndDate &&
      periodToCheck.middleSecondCheckStartDate
    ) {
      const middleFirstCheckEnd = new Date(
        periodToCheck.middleFirstCheckEndDate
      );
      const middleSecondCheckStart = new Date(
        periodToCheck.middleSecondCheckStartDate
      );
      if (middleFirstCheckEnd >= middleSecondCheckStart) {
        alert(
          "중간자료 제출의 끝 시간은 중간자료 제출(기한 후 제출)의 시작 시간보다 이전이어야 합니다."
        );
        return false;
      }
    }

    // 4. 중간자료 제출(기한 후 제출)의 끝 시간 < 최종자료 제출의 시작 시간
    if (
      periodToCheck.middleSecondCheckEndDate &&
      periodToCheck.finalFirstCheckStartDate
    ) {
      const middleSecondCheckEnd = new Date(
        periodToCheck.middleSecondCheckEndDate
      );
      const finalFirstCheckStart = new Date(
        periodToCheck.finalFirstCheckStartDate
      );
      if (middleSecondCheckEnd >= finalFirstCheckStart) {
        alert(
          "중간자료 제출(기한 후 제출)의 끝 시간은 최종자료 제출의 시작 시간보다 이전이어야 합니다."
        );
        return false;
      }
    }

    // 5. 최종자료 제출의 끝 시간 < 최종자료 제출(기한 후 제출)의 시작 시간
    if (
      periodToCheck.finalFirstCheckEndDate &&
      periodToCheck.finalSecondCheckStartDate
    ) {
      const finalFirstCheckEnd = new Date(periodToCheck.finalFirstCheckEndDate);
      const finalSecondCheckStart = new Date(
        periodToCheck.finalSecondCheckStartDate
      );
      if (finalFirstCheckEnd >= finalSecondCheckStart) {
        alert(
          "최종자료 제출의 끝 시간은 최종자료 제출(기한 후 제출)의 시작 시간보다 이전이어야 합니다."
        );
        return false;
      }
    }

    return true;
  };

  // 모든 점검과 미션의 시간이 겹치지 않는지 체크하는 함수
  const validateNoTimeOverlap = (): boolean => {
    const timeRanges: Array<{ start: Date; end: Date; name: string }> = [];

    // 점검 기간들을 수집
    if (checkPeriod.firstCheckStartDate && checkPeriod.firstCheckEndDate) {
      timeRanges.push({
        start: new Date(checkPeriod.firstCheckStartDate),
        end: new Date(checkPeriod.firstCheckEndDate),
        name: "시작자료 제출",
      });
    }
    if (
      checkPeriod.firstSecondCheckStartDate &&
      checkPeriod.firstSecondCheckEndDate
    ) {
      timeRanges.push({
        start: new Date(checkPeriod.firstSecondCheckStartDate),
        end: new Date(checkPeriod.firstSecondCheckEndDate),
        name: "시작자료 제출(기한 후 제출)",
      });
    }
    if (
      checkPeriod.middleFirstCheckStartDate &&
      checkPeriod.middleFirstCheckEndDate
    ) {
      timeRanges.push({
        start: new Date(checkPeriod.middleFirstCheckStartDate),
        end: new Date(checkPeriod.middleFirstCheckEndDate),
        name: "중간자료 제출",
      });
    }
    if (
      checkPeriod.middleSecondCheckStartDate &&
      checkPeriod.middleSecondCheckEndDate
    ) {
      timeRanges.push({
        start: new Date(checkPeriod.middleSecondCheckStartDate),
        end: new Date(checkPeriod.middleSecondCheckEndDate),
        name: "중간자료 제출(기한 후 제출)",
      });
    }
    if (
      checkPeriod.finalFirstCheckStartDate &&
      checkPeriod.finalFirstCheckEndDate
    ) {
      timeRanges.push({
        start: new Date(checkPeriod.finalFirstCheckStartDate),
        end: new Date(checkPeriod.finalFirstCheckEndDate),
        name: "최종자료 제출",
      });
    }
    if (
      checkPeriod.finalSecondCheckStartDate &&
      checkPeriod.finalSecondCheckEndDate
    ) {
      timeRanges.push({
        start: new Date(checkPeriod.finalSecondCheckStartDate),
        end: new Date(checkPeriod.finalSecondCheckEndDate),
        name: "최종자료 제출(기한 후 제출)",
      });
    }

    // 세부 미션들을 수집
    missions.forEach((mission, index) => {
      if (mission.startDate && mission.endDate) {
        timeRanges.push({
          start: new Date(mission.startDate),
          end: new Date(mission.endDate),
          name: `세부 미션 ${index + 1} (${mission.title || "제목 없음"})`,
        });
      }
    });

    // 시작자료 제출(기한 후 제출)과 세부 미션은 겹쳐도 허용
    const isFirstSecondCheck = (name: string) =>
      name === "시작자료 제출(기한 후 제출)";
    const isMission = (name: string) => name.startsWith("세부 미션 ");

    // 모든 시간 범위 쌍을 비교하여 겹치는지 체크
    for (let i = 0; i < timeRanges.length; i++) {
      for (let j = i + 1; j < timeRanges.length; j++) {
        const range1 = timeRanges[i];
        const range2 = timeRanges[j];

        if (
          (isFirstSecondCheck(range1.name) && isMission(range2.name)) ||
          (isMission(range1.name) && isFirstSecondCheck(range2.name))
        ) {
          continue; // 시작자료 제출(기한 후 제출)와 세부 미션은 겹쳐도 됨
        }

        if (
          checkTimeOverlap(range1.start, range1.end, range2.start, range2.end)
        ) {
          alert(
            `시간이 겹칩니다: "${range1.name}"과(와) "${range2.name}"의 시간이 겹칩니다. 모든 자료 제출과 미션의 시간은 겹치지 않아야 합니다.`
          );
          return false;
        }
      }
    }

    return true;
  };

  // 점검 기간의 시작 시간이 끝 시간보다 이전인지 체크하는 함수
  const validateCheckPeriodStartEndTime = (
    tempCheckPeriod?: typeof checkPeriod
  ): boolean => {
    const periodToCheck = tempCheckPeriod || checkPeriod;

    // 시작자료 제출
    if (periodToCheck.firstCheckStartDate && periodToCheck.firstCheckEndDate) {
      const start = new Date(periodToCheck.firstCheckStartDate);
      const end = new Date(periodToCheck.firstCheckEndDate);
      if (start >= end) {
        alert(
          "시작자료 제출의 시작 시간은 끝 시간보다 이전이어야 합니다."
        );
        return false;
      }
    }

    // 시작자료 제출(기한 후 제출)
    if (
      periodToCheck.firstSecondCheckStartDate &&
      periodToCheck.firstSecondCheckEndDate
    ) {
      const start = new Date(periodToCheck.firstSecondCheckStartDate);
      const end = new Date(periodToCheck.firstSecondCheckEndDate);
      if (start >= end) {
        alert(
          "시작자료 제출(기한 후 제출)의 시작 시간은 끝 시간보다 이전이어야 합니다."
        );
        return false;
      }
    }

    // 중간자료 제출
    if (
      periodToCheck.middleFirstCheckStartDate &&
      periodToCheck.middleFirstCheckEndDate
    ) {
      const start = new Date(periodToCheck.middleFirstCheckStartDate);
      const end = new Date(periodToCheck.middleFirstCheckEndDate);
      if (start >= end) {
        alert(
          "중간자료 제출의 시작 시간은 끝 시간보다 이전이어야 합니다."
        );
        return false;
      }
    }

    // 중간자료 제출(기한 후 제출)
    if (
      periodToCheck.middleSecondCheckStartDate &&
      periodToCheck.middleSecondCheckEndDate
    ) {
      const start = new Date(periodToCheck.middleSecondCheckStartDate);
      const end = new Date(periodToCheck.middleSecondCheckEndDate);
      if (start >= end) {
        alert(
          "중간자료 제출(기한 후 제출)의 시작 시간은 끝 시간보다 이전이어야 합니다."
        );
        return false;
      }
    }

    // 최종자료 제출
    if (
      periodToCheck.finalFirstCheckStartDate &&
      periodToCheck.finalFirstCheckEndDate
    ) {
      const start = new Date(periodToCheck.finalFirstCheckStartDate);
      const end = new Date(periodToCheck.finalFirstCheckEndDate);
      if (start >= end) {
        alert(
          "최종자료 제출의 시작 시간은 끝 시간보다 이전이어야 합니다."
        );
        return false;
      }
    }

    // 최종자료 제출(기한 후 제출)
    if (
      periodToCheck.finalSecondCheckStartDate &&
      periodToCheck.finalSecondCheckEndDate
    ) {
      const start = new Date(periodToCheck.finalSecondCheckStartDate);
      const end = new Date(periodToCheck.finalSecondCheckEndDate);
      if (start >= end) {
        alert(
          "최종자료 제출(기한 후 제출)의 시작 시간은 끝 시간보다 이전이어야 합니다."
        );
        return false;
      }
    }

    return true;
  };

  // 점검 기간 변경 핸들러 (시작 시간 validation 추가)
  const handleCheckPeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    // 시작 시간 필드인 경우 validation
    if (name.includes("StartDate") && value) {
      if (!validateStartTime(value)) {
        return; // validation 실패 시 변경하지 않음
      }
    }

    // 변경할 값을 임시로 적용한 상태 생성
    const tempCheckPeriod = { ...checkPeriod, [name]: value };

    // 시작 시간이 끝 시간보다 이전인지 체크
    if (!validateCheckPeriodStartEndTime(tempCheckPeriod)) {
      return; // validation 실패 시 변경하지 않음
    }

    // 점검 순차 검증
    if (!validateCheckPeriodSequence(tempCheckPeriod)) {
      return; // validation 실패 시 변경하지 않음
    }

    onChangeCheckPeriod(e);
  };

  // 미션 변경 핸들러 (시작 시간 validation 및 같은 날짜 체크 추가)
  const handleMissionChange = (
    index: number,
    key: keyof ChallengeMission,
    value: string
  ) => {
    // 시작 시간인 경우 validation
    if (key === "startDate" && value) {
      if (!validateStartTime(value)) {
        return; // validation 실패 시 변경하지 않음
      }
    }

    // 시작 시간 또는 끝 시간 변경 시 같은 날짜인지 체크
    if ((key === "startDate" || key === "endDate") && value) {
      const updatedMission = { ...missions[index], [key]: value };
      const startDate = updatedMission.startDate;
      const endDate = updatedMission.endDate;

      // 시작 시간과 끝 시간이 모두 입력된 경우에만 체크
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);

        // 시작 시간이 끝 시간보다 이전인지 체크
        if (start >= end) {
          alert("세부 미션의 시작 시간은 끝 시간보다 이전이어야 합니다.");
          return; // validation 실패 시 변경하지 않음
        }

        // KST 기준 같은 날짜인지 체크 (년, 월, 일 비교)
        const startYear = start.getFullYear();
        const startMonth = start.getMonth();
        const startDay = start.getDate();

        const endYear = end.getFullYear();
        const endMonth = end.getMonth();
        const endDay = end.getDate();

        if (
          startYear !== endYear ||
          startMonth !== endMonth ||
          startDay !== endDay
        ) {
          alert("세부 미션의 시작 시간과 끝 시간은 같은 날짜로 설정해주세요.");
          return; // validation 실패 시 변경하지 않음
        }
      }
    }

    onMissionChange(index, key, value);
  };

  const onCreate = async (): Promise<boolean> => {
    // 점검 기간의 시작 시간이 끝 시간보다 이전인지 체크
    if (!validateCheckPeriodStartEndTime()) {
      return false;
    }

    // 모든 점검과 미션의 시간이 겹치지 않는지 체크
    if (!validateNoTimeOverlap()) {
      return false;
    }

    // 세부 미션의 시작 시간 validation (KST 기준)
    for (let i = 0; i < missions.length; i++) {
      const mission = missions[i];
      if (mission.startDate) {
        // datetime-local은 로컬 시간(KST)으로 입력되므로, 로컬 시간 기준으로 체크
        const date = new Date(mission.startDate);
        const localHours = date.getHours(); // 로컬 시간의 시간 (한국에서 사용하면 KST)
        const localMinutes = date.getMinutes(); // 로컬 시간의 분 (한국에서 사용하면 KST)
        // 18:00:00은 허용, 18:00:01부터는 차단
        if (localHours > 18 || (localHours === 18 && localMinutes > 0)) {
          alert(
            "세부 미션의 시작 시간은 한국시간(KST) 오후 6시(18:00) 이전으로 설정해주세요."
          );
          return false;
        }
      }

      // 세부 미션의 시작 시간과 끝 시간이 같은 날짜인지 체크
      if (mission.startDate && mission.endDate) {
        const start = new Date(mission.startDate);
        const end = new Date(mission.endDate);

        // 시작 시간이 끝 시간보다 이전인지 체크
        if (start >= end) {
          alert("세부 미션의 시작 시간은 끝 시간보다 이전이어야 합니다.");
          return false;
        }

        // KST 기준 같은 날짜인지 체크 (년, 월, 일 비교)
        const startYear = start.getFullYear();
        const startMonth = start.getMonth();
        const startDay = start.getDate();

        const endYear = end.getFullYear();
        const endMonth = end.getMonth();
        const endDay = end.getDate();

        if (
          startYear !== endYear ||
          startMonth !== endMonth ||
          startDay !== endDay
        ) {
          alert("세부 미션의 시작 시간과 끝 시간은 같은 날짜로 설정해주세요.");
          return false;
        }
      }
    }

    const basicMissions = changeCheckPeriodIntoMission(checkPeriod, null);
    if (basicMissions.length === 0) {
      return false;
    }

    const additionalMissions = missions.map((mission) => {
      return {
        ...mission,
        startDate: new Date(mission.startDate),
        endDate: new Date(mission.endDate),
        missionType: MissionType.MISSION,
      };
    });

    const request: ChallengeCreate = {
      ...data,
      paymentStartDate: new Date(data.paymentStartDate),
      paymentEndDate: new Date(data.paymentEndDate),
      startDate: new Date(data.startDate),
      endDate: new Date(data.endDate),
      price: Number(data.price),
      missions: [...additionalMissions, ...basicMissions],
    };

    if (!validateChallengeData(request)) {
      return false;
    }

    try {
      const { challenge } = await challengeRepository.createAdmin(request);
      await Promise.all(
        images.map(async (image) => {
          const { url } = await challengePictureRepository.addChallengePicture(
            challenge.id,
            image.name
          );

          await fetch(url, {
            method: "PUT",
            body: image,
            headers: {
              "Content-Type": image.type,
            },
          });
          // await challengePictureRepository.uploadChallengePicturePresignedUrl(
          //   url,
          //   image
          // );
        })
      );
      return true;
    } catch (error) {
      console.error("챌린지 생성 실패:", error);
      return false;
    }
  };

  const onUpdate = async (): Promise<boolean> => {
    if (challenge === null) return false;

    // 점검 기간의 시작 시간이 끝 시간보다 이전인지 체크
    if (!validateCheckPeriodStartEndTime()) {
      return false;
    }

    // 모든 점검과 미션의 시간이 겹치지 않는지 체크
    if (!validateNoTimeOverlap()) {
      return false;
    }

    // 세부 미션의 시작 시간 validation (KST 기준)
    for (let i = 0; i < missions.length; i++) {
      const mission = missions[i];
      if (mission.startDate) {
        // datetime-local은 로컬 시간(KST)으로 입력되므로, 로컬 시간 기준으로 체크
        const date = new Date(mission.startDate);
        const localHours = date.getHours(); // 로컬 시간의 시간 (한국에서 사용하면 KST)
        const localMinutes = date.getMinutes(); // 로컬 시간의 분 (한국에서 사용하면 KST)
        // 18:00:00은 허용, 18:00:01부터는 차단
        if (localHours > 18 || (localHours === 18 && localMinutes > 0)) {
          alert(
            "세부 미션의 시작 시간은 한국시간(KST) 오후 6시(18:00) 이전으로 설정해주세요."
          );
          return false;
        }
      }

      // 세부 미션의 시작 시간과 끝 시간이 같은 날짜인지 체크
      if (mission.startDate && mission.endDate) {
        const start = new Date(mission.startDate);
        const end = new Date(mission.endDate);

        // 시작 시간이 끝 시간보다 이전인지 체크
        if (start >= end) {
          alert("세부 미션의 시작 시간은 끝 시간보다 이전이어야 합니다.");
          return false;
        }

        // KST 기준 같은 날짜인지 체크 (년, 월, 일 비교)
        const startYear = start.getFullYear();
        const startMonth = start.getMonth();
        const startDay = start.getDate();

        const endYear = end.getFullYear();
        const endMonth = end.getMonth();
        const endDay = end.getDate();

        if (
          startYear !== endYear ||
          startMonth !== endMonth ||
          startDay !== endDay
        ) {
          alert("세부 미션의 시작 시간과 끝 시간은 같은 날짜로 설정해주세요.");
          return false;
        }
      }
    }

    const challengeId = challenge.challenge.id;

    const updateChallenge = new UpdateChallengeDto({
      ...data,
      id: challengeId,
      paymentStartDate: new Date(data.paymentStartDate),
      paymentEndDate: new Date(data.paymentEndDate),
      startDate: new Date(data.startDate),
      endDate: new Date(data.endDate),
      price: Number(data.price),
    });

    try {
      await challengeRepository.updateAdmin(updateChallenge);

      const basicMissions = changeCheckPeriodIntoMission(
        checkPeriod,
        challenge
      ).filter(
        (mission) => mission.challengeId !== null
      ) as UpdateChallengeMissionRequestDto[];

      if (basicMissions.length === 0) {
        return false;
      }

      const additionalMissions = missions.map((mission) => {
        return {
          ...mission,
          challengeId: challenge.challenge.id,
          startDate: new Date(mission.startDate),
          endDate: new Date(mission.endDate),
          missionType: MissionType.MISSION,
        };
      });

      await Promise.all(
        deleteMissionsList.map(async (id) => {
          return await challengeMissionRepository.deleteChallengeMission(id);
        })
      );

      await challengeMissionRepository.updateChallengeMission([
        ...basicMissions,
        ...additionalMissions,
      ]);

      if (images.length > 0) {
        await Promise.all(
          images.map(async (image) => {
            const { url } =
              await challengePictureRepository.addChallengePicture(
                challengeId,
                image.name
              );

            await fetch(url, {
              method: "PUT",
              body: image,
              headers: {
                "Content-Type": image.type,
              },
            });
            // await challengePictureRepository.uploadChallengePicturePresignedUrl(
            //   url,
            //   image
            // );
          })
        );
      }

      if (deleteImages.length > 0) {
        await challengePictureRepository.deleteChallengePictures(
          challengeId,
          deleteImages
        );
      }

      return true;
    } catch (error) {
      console.error("챌린지 수정 실패:", error);
      return false;
    }
  };

  return (
    <ModalBox width={1200} style={{ maxHeight: "90vh", overflowY: "scroll" }}>
      <ModalTitle>
        {challengeInfo === ChallengeModal.CREATE
          ? "챌린지 개설하기"
          : challengeInfo === ChallengeModal.UPDATE
          ? "챌린지 수정하기"
          : ""}
      </ModalTitle>
      <RowBox
        style={{
          width: "100%",
          overflowY: "scroll",
          display: "flex",
          flexDirection: "column",
        }}
        height="auto"
        justifyContent="space-between"
      >
        <ColumnBox style={{ height: "auto", overflow: "visible" }}>
          <ModalComponent
            description="이름"
            component={
              <StandardInput
                placeholder="챌린지명"
                name="title"
                value={data.title}
                onChange={onChange}
              />
            }
          />
          <ModalComponent
            marginTop={20}
            description="결제 가능 기간"
            component={
              <RowBox height="auto" justifyContent="space-between">
                <StandardInput
                  type="datetime-local"
                  name="paymentStartDate"
                  value={data.paymentStartDate}
                  onChange={onChange}
                />
                <Box
                  sx={{
                    width: "12px",
                    margin: "8px",
                  }}
                >
                  ~
                </Box>
                <StandardInput
                  type="datetime-local"
                  name="paymentEndDate"
                  value={data.paymentEndDate}
                  onChange={onChange}
                />
              </RowBox>
            }
          />
          <ModalComponent
            marginTop={20}
            description="챌린지 진행 기간"
            component={
              <RowBox height="auto" justifyContent="space-between">
                <StandardInput
                  type="datetime-local"
                  name="startDate"
                  value={data.startDate}
                  onChange={onChange}
                />
                <Box
                  sx={{
                    width: "12px",
                    margin: "8px",
                  }}
                >
                  ~
                </Box>
                <StandardInput
                  type="datetime-local"
                  name="endDate"
                  value={data.endDate}
                  onChange={onChange}
                />
              </RowBox>
            }
          />
          <ModalComponent
            marginTop={20}
            description="가격 (원)"
            component={
              <StandardInput
                name="price"
                value={data.price}
                onChange={onChange}
              />
            }
          />
          <ModalComponent
            marginTop={20}
            description="사진 또는 영상 (960px×540px 이상)"
            component={
              <RowBox justifyContent="flex-start">
                {data.pictures.map(({ key, url }, index) => {
                  return (
                    <div
                      style={{
                        width: "150px",
                        height: "150px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <button
                        onClick={() => {
                          setDeleteImages([...deleteImages, key]);
                          setData({
                            ...data,
                            pictures: data.pictures.filter(
                              (picture) => picture.key !== key
                            ),
                          });
                        }}
                        style={{
                          position: "absolute",
                          top: "0",
                          right: "0",
                          backgroundColor: "white",
                          width: "50px",
                          height: "30px",
                          borderRadius: "8px",
                        }}
                      >
                        삭제
                      </button>
                      <StandardImage
                        onClick={() => {
                          var newTab = window.open();
                          if (newTab) {
                            newTab.document.body.innerHTML = `<img src="${url}" width="100%" />`;
                          }
                        }}
                        key={index}
                        src={url}
                        alt="image"
                      />
                    </div>
                  );
                })}
                {images.map((image, index) => {
                  const url = URL.createObjectURL(image);
                  return (
                    <div
                      style={{
                        width: "150px",
                        height: "150px",
                        marginLeft: "16px",
                        borderRadius: "8px",
                        overflow: "hidden",
                        position: "relative",
                      }}
                    >
                      <button
                        onClick={() => {
                          setImages(images.filter((_, i) => i !== index));
                        }}
                        style={{
                          position: "absolute",
                          top: "0",
                          right: "0",
                          backgroundColor: "white",
                          width: "50px",
                          height: "30px",
                          borderRadius: "8px",
                        }}
                      >
                        삭제
                      </button>
                      <StandardImage key={index} src={url} alt="image" />{" "}
                    </div>
                  );
                })}
                <ImageInput
                  marginLeft={
                    data.pictures.length > 0 || images.length > 0 ? 16 : 0
                  }
                  fileInput={fileInput}
                  onFileUpload={onFileUpload}
                  handleChange={handleFileChange}
                />
              </RowBox>
            }
          />
          <ModalComponent
            marginTop={20}
            description="내용"
            component={
              <StandardTextArea
                placeholder="내용을 입력해주세요."
                height={120}
                name="description"
                value={data.description}
                onChange={(e) => {
                  setData({
                    ...data,
                    description: e.target.value,
                  });
                }}
              />
            }
          />
        </ColumnBox>
        <ColumnBox style={{ overflow: "visible" }}>
          <TimeConstraintBox>
            <TimeConstraintTitle>⏰ 시간 설정 안내</TimeConstraintTitle>
            <TimeConstraintItem>
              <strong>1. 시작 시간 제한</strong>
              <br />
              모든 자료 제출과 미션의 시작 시간은 한국시간(KST) 기준 오후 6시(18:00)
              이전으로 설정해야 합니다.
            </TimeConstraintItem>
            <TimeConstraintItem>
              <strong>2. 시작 시간과 끝 시간</strong>
              <br />
              모든 자료 제출과 미션의 시작 시간은 끝 시간보다 이전이어야 합니다.
            </TimeConstraintItem>
            <TimeConstraintItem>
              <strong>3. 자료 제출 순서</strong>
              <br />
              자료 제출 항목들은 다음 순서대로 진행되어야 하며, 각 항목의 끝 시간은
              다음 항목의 시작 시간보다 이전이어야 합니다:
              <TimeConstraintList>
                <TimeConstraintListItem>
                  시작자료 제출
                </TimeConstraintListItem>
                <TimeConstraintListItem>
                  시작자료 제출(기한 후 제출)
                </TimeConstraintListItem>
                <TimeConstraintListItem>
                  중간자료 제출
                </TimeConstraintListItem>
                <TimeConstraintListItem>
                  중간자료 제출(기한 후 제출)
                </TimeConstraintListItem>
                <TimeConstraintListItem>
                  최종자료 제출
                </TimeConstraintListItem>
                <TimeConstraintListItem>
                  최종자료 제출(기한 후 제출)
                </TimeConstraintListItem>
              </TimeConstraintList>
            </TimeConstraintItem>
            <TimeConstraintItem>
              <strong>4. 미션 시간 설정</strong>
              <br />
              자료 제출을 제외한 세부 미션의 시작 시간과 끝 시간은 같은 날짜(KST
              기준)로 설정해야 합니다.
            </TimeConstraintItem>
          </TimeConstraintBox>
          <ModalComponent
            minHeight={60}
            description="시작자료 제출 기간"
            component={
              <RowBox height="auto" justifyContent="space-between">
                <StandardInput
                  type="datetime-local"
                  name="firstCheckStartDate"
                  value={checkPeriod.firstCheckStartDate}
                  onChange={handleCheckPeriodChange}
                />
                <Box
                  sx={{
                    width: "12px",
                    margin: "8px",
                  }}
                >
                  ~
                </Box>
                <StandardInput
                  type="datetime-local"
                  name="firstCheckEndDate"
                  value={checkPeriod.firstCheckEndDate}
                  onChange={handleCheckPeriodChange}
                />
              </RowBox>
            }
          />
          <ModalComponent
            minHeight={60}
            marginTop={20}
            description="시작자료 제출 기간(기한 후 제출)"
            component={
              <RowBox height="auto" justifyContent="space-between">
                <StandardInput
                  type="datetime-local"
                  name="firstSecondCheckStartDate"
                  value={checkPeriod.firstSecondCheckStartDate}
                  onChange={handleCheckPeriodChange}
                />
                <Box
                  sx={{
                    width: "12px",
                    margin: "8px",
                  }}
                >
                  ~
                </Box>
                <StandardInput
                  type="datetime-local"
                  name="firstSecondCheckEndDate"
                  value={checkPeriod.firstSecondCheckEndDate}
                  onChange={handleCheckPeriodChange}
                />
              </RowBox>
            }
          />
          <ModalComponent
            minHeight={60}
            marginTop={20}
            description="중간자료 제출 기간"
            component={
              <RowBox height="auto" justifyContent="space-between">
                <StandardInput
                  type="datetime-local"
                  name="middleFirstCheckStartDate"
                  value={checkPeriod.middleFirstCheckStartDate}
                  onChange={handleCheckPeriodChange}
                />
                <Box
                  sx={{
                    width: "12px",
                    margin: "8px",
                  }}
                >
                  ~
                </Box>
                <StandardInput
                  type="datetime-local"
                  name="middleFirstCheckEndDate"
                  value={checkPeriod.middleFirstCheckEndDate}
                  onChange={handleCheckPeriodChange}
                />
              </RowBox>
            }
          />
          <ModalComponent
            minHeight={60}
            marginTop={20}
            description="중간자료 제출 기간(기한 후 제출)"
            component={
              <RowBox height="auto" justifyContent="space-between">
                <StandardInput
                  type="datetime-local"
                  name="middleSecondCheckStartDate"
                  value={checkPeriod.middleSecondCheckStartDate}
                  onChange={handleCheckPeriodChange}
                />
                <Box
                  sx={{
                    width: "12px",
                    margin: "8px",
                  }}
                >
                  ~
                </Box>
                <StandardInput
                  type="datetime-local"
                  name="middleSecondCheckEndDate"
                  value={checkPeriod.middleSecondCheckEndDate}
                  onChange={handleCheckPeriodChange}
                />
              </RowBox>
            }
          />
          <ModalComponent
            minHeight={60}
            marginTop={20}
            description="최종자료 제출 기간"
            component={
              <RowBox height="auto" justifyContent="space-between">
                <StandardInput
                  type="datetime-local"
                  name="finalFirstCheckStartDate"
                  value={checkPeriod.finalFirstCheckStartDate}
                  onChange={handleCheckPeriodChange}
                />
                <Box
                  sx={{
                    width: "12px",
                    margin: "8px",
                  }}
                >
                  ~
                </Box>
                <StandardInput
                  type="datetime-local"
                  name="finalFirstCheckEndDate"
                  value={checkPeriod.finalFirstCheckEndDate}
                  onChange={handleCheckPeriodChange}
                />
              </RowBox>
            }
          />
          <ModalComponent
            minHeight={60}
            marginTop={20}
            description="최종자료 제출 기간(기한 후 제출)"
            component={
              <RowBox height="auto" justifyContent="space-between">
                <StandardInput
                  type="datetime-local"
                  name="finalSecondCheckStartDate"
                  value={checkPeriod.finalSecondCheckStartDate}
                  onChange={handleCheckPeriodChange}
                />
                <Box
                  sx={{
                    width: "12px",
                    margin: "8px",
                  }}
                >
                  ~
                </Box>
                <StandardInput
                  type="datetime-local"
                  name="finalSecondCheckEndDate"
                  value={checkPeriod.finalSecondCheckEndDate}
                  onChange={handleCheckPeriodChange}
                />
              </RowBox>
            }
          />
          <RowBox
            style={{ minHeight: "18px" }}
            marginTop={20}
            height="auto"
            justifyContent="flex-start"
          >
            <SemiTitle>세부 미션 설정하기</SemiTitle>
            <AddIcon
              onClick={onMissionAdd}
              sx={{ color: "#323232", cursor: "pointer", fontSize: "18px" }}
            />
          </RowBox>
          {missions.map((mission, index) => {
            return (
              <ChallengeMissionComponent
                key={index}
                index={index}
                mission={mission}
                onMissionDelete={onMissionDelete}
                onMissionChange={handleMissionChange}
              />
            );
          })}
        </ColumnBox>
      </RowBox>
      <MultiButton
        sub="취소"
        main="저장"
        marginTop={32}
        onSubClick={() => setChallengeInfo(null)}
        onMainClick={async () => {
          if (challengeInfo === ChallengeModal.CREATE) {
            const success = await onCreate();
            if (success) {
              // 모달을 닫아서 ChallengeContent의 useEffect가 트리거되도록 함
              setChallengeInfo(null);
            }
          } else if (challengeInfo === ChallengeModal.UPDATE) {
            const success = await onUpdate();
            if (success) {
              // 모달을 닫아서 ChallengeContent의 useEffect가 트리거되도록 함
              setChallengeInfo(null);
            }
          } else {
            setChallengeInfo(null);
          }
        }}
      />
    </ModalBox>
  );
}

function changeCheckPeriodIntoMission(
  checkPeriod: {
    firstCheckStartDate: string;
    firstCheckEndDate: string;
    firstSecondCheckStartDate: string;
    firstSecondCheckEndDate: string;
    middleFirstCheckStartDate: string;
    middleFirstCheckEndDate: string;
    middleSecondCheckStartDate: string;
    middleSecondCheckEndDate: string;
    finalFirstCheckStartDate: string;
    finalFirstCheckEndDate: string;
    finalSecondCheckStartDate: string;
    finalSecondCheckEndDate: string;
  },
  challenge?: ChallengeAdminDto | null
) {
  const missions = [];
  const {
    firstCheckStartDate,
    firstCheckEndDate,
    firstSecondCheckStartDate,
    firstSecondCheckEndDate,
    middleFirstCheckStartDate,
    middleFirstCheckEndDate,
    middleSecondCheckStartDate,
    middleSecondCheckEndDate,
    finalFirstCheckStartDate,
    finalFirstCheckEndDate,
    finalSecondCheckStartDate,
    finalSecondCheckEndDate,
  } = checkPeriod;

  if (
    !firstCheckStartDate ||
    !firstCheckEndDate ||
    !firstSecondCheckStartDate ||
    !firstSecondCheckEndDate ||
    !middleFirstCheckStartDate ||
    !middleFirstCheckEndDate ||
    !middleSecondCheckStartDate ||
    !middleSecondCheckEndDate ||
    !finalFirstCheckStartDate ||
    !finalFirstCheckEndDate ||
    !finalSecondCheckStartDate ||
    !finalSecondCheckEndDate
  ) {
    alert("자료 제출 기간을 모두 입력해주세요.");
    return [];
  }

  // 시작 시간이 한국시간(KST) 오후 6시(18:00)를 넘는지 체크
  const validateStartTime = (datetimeString: string): boolean => {
    if (!datetimeString) return true;
    const date = new Date(datetimeString);
    // 로컬 시간대의 시간과 분을 가져옴 (한국에서 사용하면 KST 기준)
    const localHours = date.getHours();
    const localMinutes = date.getMinutes();
    // 18:00:00은 허용, 18:00:01부터는 차단
    return localHours < 18 || (localHours === 18 && localMinutes === 0);
  };

  const startTimes = [
    firstCheckStartDate,
    firstSecondCheckStartDate,
    middleFirstCheckStartDate,
    middleSecondCheckStartDate,
    finalFirstCheckStartDate,
    finalSecondCheckStartDate,
  ];

  for (const startTime of startTimes) {
    if (startTime && !validateStartTime(startTime)) {
      alert("자료 제출 시작 시간은 오후 6시(18:00) 이전으로 설정해주세요.");
      return [];
    }
  }

  if (firstCheckStartDate && firstCheckEndDate) {
    missions.push({
      id: findMissionId(challenge || null, MissionType.CHECK_FIRST),
      challengeId: challenge?.challenge.id || null,
      title: "시작자료 제출",
      description: "시작자료 제출",
      startDate: new Date(firstCheckStartDate),
      endDate: new Date(firstCheckEndDate),
      missionType: MissionType.CHECK_FIRST,
    });
  }
  if (firstSecondCheckStartDate && firstSecondCheckEndDate) {
    missions.push({
      id: findMissionId(challenge || null, MissionType.CHECK_FIRST_2),
      challengeId: challenge?.challenge.id || null,
      title: "시작자료 제출(기한 후 제출)",
      description: "시작자료 제출(기한 후 제출)",
      startDate: new Date(firstSecondCheckStartDate),
      endDate: new Date(firstSecondCheckEndDate),
      missionType: MissionType.CHECK_FIRST_2,
    });
  }
  if (middleFirstCheckStartDate && middleFirstCheckEndDate) {
    missions.push({
      id: findMissionId(challenge || null, MissionType.CHECK_MIDDLE_1),
      challengeId: challenge?.challenge.id || null,
      title: "중간자료 제출",
      description: "중간자료 제출",
      startDate: new Date(middleFirstCheckStartDate),
      endDate: new Date(middleFirstCheckEndDate),
      missionType: MissionType.CHECK_MIDDLE_1,
    });
  }
  if (middleSecondCheckStartDate && middleSecondCheckEndDate) {
    missions.push({
      id: findMissionId(challenge || null, MissionType.CHECK_MIDDLE_2),
      challengeId: challenge?.challenge.id || null,
      title: "중간자료 제출(기한 후 제출)",
      description: "중간자료 제출(기한 후 제출)",
      startDate: new Date(middleSecondCheckStartDate),
      endDate: new Date(middleSecondCheckEndDate),
      missionType: MissionType.CHECK_MIDDLE_2,
    });
  }
  if (finalFirstCheckStartDate && finalFirstCheckEndDate) {
    missions.push({
      id: findMissionId(challenge || null, MissionType.CHECK_LAST_1),
      challengeId: challenge?.challenge.id || null,
      title: "최종자료 제출",
      description: "최종자료 제출",
      startDate: new Date(finalFirstCheckStartDate),
      endDate: new Date(finalFirstCheckEndDate),
      missionType: MissionType.CHECK_LAST_1,
    });
  }
  if (finalSecondCheckStartDate && finalSecondCheckEndDate) {
    missions.push({
      id: findMissionId(challenge || null, MissionType.CHECK_LAST_2),
      challengeId: challenge?.challenge.id || null,
      title: "최종자료 제출(기한 후 제출)",
      description: "최종자료 제출(기한 후 제출)",
      startDate: new Date(finalSecondCheckStartDate),
      endDate: new Date(finalSecondCheckEndDate),
      missionType: MissionType.CHECK_LAST_2,
    });
  }
  return missions;
}

function findMissionId(challenge: ChallengeAdminDto | null, type: MissionType) {
  if (!challenge) return null;

  const { missions } = challenge;
  const mission = missions.find((mission) => mission.missionType === type);
  return mission?.id || null;
}

function getMissionCheckPeriods(challenge: ChallengeAdminDto | null): {
  firstCheckStartDate: string;
  firstCheckEndDate: string;
  firstSecondCheckStartDate: string;
  firstSecondCheckEndDate: string;
  middleFirstCheckStartDate: string;
  middleFirstCheckEndDate: string;
  middleSecondCheckStartDate: string;
  middleSecondCheckEndDate: string;
  finalFirstCheckStartDate: string;
  finalFirstCheckEndDate: string;
  finalSecondCheckStartDate: string;
  finalSecondCheckEndDate: string;
} {
  if (!challenge) {
    return {
      firstCheckStartDate: "",
      firstCheckEndDate: "",
      firstSecondCheckStartDate: "",
      firstSecondCheckEndDate: "",
      middleFirstCheckStartDate: "",
      middleFirstCheckEndDate: "",
      middleSecondCheckStartDate: "",
      middleSecondCheckEndDate: "",
      finalFirstCheckStartDate: "",
      finalFirstCheckEndDate: "",
      finalSecondCheckStartDate: "",
      finalSecondCheckEndDate: "",
    };
  }

  const { missions } = challenge;
  const data = {
    firstCheckStartDate: "",
    firstCheckEndDate: "",
    firstSecondCheckStartDate: "",
    firstSecondCheckEndDate: "",
    middleFirstCheckStartDate: "",
    middleFirstCheckEndDate: "",
    middleSecondCheckStartDate: "",
    middleSecondCheckEndDate: "",
    finalFirstCheckStartDate: "",
    finalFirstCheckEndDate: "",
    finalSecondCheckStartDate: "",
    finalSecondCheckEndDate: "",
  };
  missions.forEach((mission) => {
    const { startDate, endDate, missionType } = mission;
    const start = formatDateToYYYYMMDDTHHMM(new Date(startDate));
    const end = formatDateToYYYYMMDDTHHMM(new Date(endDate));
    switch (missionType) {
      case MissionType.CHECK_FIRST:
        data.firstCheckStartDate = start;
        data.firstCheckEndDate = end;
        break;
      case MissionType.CHECK_FIRST_2:
        data.firstSecondCheckStartDate = start;
        data.firstSecondCheckEndDate = end;
        break;
      case MissionType.CHECK_MIDDLE_1:
        data.middleFirstCheckStartDate = start;
        data.middleFirstCheckEndDate = end;
        break;
      case MissionType.CHECK_MIDDLE_2:
        data.middleSecondCheckStartDate = start;
        data.middleSecondCheckEndDate = end;
        break;
      case MissionType.CHECK_LAST_1:
        data.finalFirstCheckStartDate = start;
        data.finalFirstCheckEndDate = end;
        break;
      case MissionType.CHECK_LAST_2:
        data.finalSecondCheckStartDate = start;
        data.finalSecondCheckEndDate = end;
        break;
    }
  });
  return data;
}

function validateChallengeData(data: ChallengeCreate) {
  const {
    title,
    paymentStartDate,
    paymentEndDate,
    startDate,
    endDate,
    price,
    description,
  } = data;
  if (!title) {
    alert("챌린지 이름을 입력해주세요.");
    return false;
  }
  if (!paymentStartDate) {
    alert("결제 가능 기간을 입력해주세요.");
    return false;
  }
  if (!paymentEndDate) {
    alert("결제 가능 기간을 입력해주세요.");
    return false;
  }
  if (!startDate) {
    alert("챌린지 시작 기간을 입력해주세요.");
    return false;
  }
  if (!endDate) {
    alert("챌린지 종료 기간을 입력해주세요.");
    return false;
  }
  if (!price) {
    alert("가격을 입력해주세요.");
    return false;
  }
  if (!description) {
    alert("내용을 입력해주세요.");
    return false;
  }
  return true;
}

const ColumnBox = styled.div`
  width: 100%;
  height: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  overflow: visible;
`;

const SemiTitle = styled.p`
  font-size: 16px;
  color: #323232;

  overflow: hidden;
`;

const TimeConstraintBox = styled.div`
  width: 100%;
  padding: 16px;
  margin-top: 20px;
  margin-bottom: 20px;
  background-color: #f5f5f5;
  border-radius: 8px;
  border-left: 4px solid #4a90e2;
`;

const TimeConstraintTitle = styled.p`
  font-size: 16px;
  font-weight: 600;
  color: #323232;
  margin-bottom: 12px;
`;

const TimeConstraintItem = styled.div`
  font-size: 14px;
  color: #555;
  line-height: 1.6;
  margin-bottom: 8px;
  padding-left: 8px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TimeConstraintList = styled.ul`
  margin: 0;
  padding-left: 20px;
`;

const TimeConstraintListItem = styled.li`
  font-size: 14px;
  color: #555;
  line-height: 1.6;
  margin-bottom: 4px;
`;
